import React, { useState, useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { ThemeContext } from 'styled-components';
import { status } from 'constants/general';
import { setOrderDispensedItemsDetails } from 'modules/labs/orders/actions';
import {
  promoCodeStatusPending,
  orderStatusToEnableAddingPromoCode,
} from 'modules/labs/orders/enums';
import Loader from 'modules/shared/common/components/Loader';
import SectionDivider from 'modules/shared/common/components/SectionDivider';
import InputField from 'modules/shared/common/components/InputField';
import Can from 'modules/shared/common/components/Can';
import {
  OrderDetailsInvoiceSection,
  OrderDetailsInvoiceTitle,
  OrderDetailsInvoiceHeaderWrapper,
  OrderDetailsInvoiceHeaderFilterWrapper,
  OrderDetailsInvoiceHeaderFilterItem,
  OrderDetailsInvoiceMultiGridWrapper,
  OrderDetailsInvoiceGridWrapper,
  OrderDetailsInvoiceGridHeader,
  OrderDetailsInvoiceGridItem,
  OrderDetailsInvoiceGridItemIcon,
  OrderDetailsInvoiceGridQuantityWrapper,
  OrderDetailsInvoiceGridChangeQuantityWrapper,
  OrderDetailsInvoiceGridChangeQuantityValue,
  OrderDetailsInvoiceGridChangeQuantityMinus,
  OrderDetailsInvoiceGridChangeQuantityPlus,
  OrderDetailsInvoiceSearchBarWrapper,
  OrderDetailsInvoiceSearchBarInputWrapper,
  OrderDetailsInvoiceSearchBar,
  OrderDetailsInvoiceSearchBarIcon,
  OrderDetailsInvoiceSearchBarEnvelop,
  OrderDetailsInvoiceSearchBarLoaderWrapper,
  OrderDetailsInvoiceSearchBarNoDataText,
  OrderDetailsInvoiceSearchBarItemWrapper,
  OrderDetailsInvoiceSearchBarItem,
  OrderDetailsInvoiceSearchBarItemSubText,
  OrderDetailsInvoiceInfoWrapper,
  OrderDetailsInvoiceInfoFirstColumn,
  OrderDetailsInvoiceInfoSecondColumn,
  OrderInvoiceInfoPromoCode,
  OrderDetailsInvoiceInfoText,
  OrderDetailsInvoiceInfoBlackText,
  OrderDetailsInvoiceInfoBigText,
  OrderDetailsInvoiceInfoIconText,
  OrderDetailsInvoiceBtnWrapper,
  OrderDetailsInvoiceBtn,
  extendOrderDetailsInvoiceLoaderStyle,
  InvalidPromoCodeText,
  OrderInvoiceRemovePromoCodeItemIcon,
} from './OrderInvoice.style';

// ###############################################################
// <---------------------- Sub Sections ------------------------->
// ###############################################################
const OrderInvoiceHeader = ({
  stringsData,
  selectedFilter,
  invoiceHeaderMenuItemsObj,
  setSelectedFilter,
}) => (
  <OrderDetailsInvoiceHeaderWrapper>
    <OrderDetailsInvoiceTitle>
      {stringsData.orderDetailsContent.orderInvoiceTitle}
    </OrderDetailsInvoiceTitle>
    <OrderDetailsInvoiceHeaderFilterWrapper>
      {Object.keys(invoiceHeaderMenuItemsObj).map(menuItemKey => (
        <OrderDetailsInvoiceHeaderFilterItem
          key={menuItemKey}
          selected={selectedFilter === invoiceHeaderMenuItemsObj[menuItemKey]}
          onClick={() => setSelectedFilter(invoiceHeaderMenuItemsObj[menuItemKey])}
        >
          {invoiceHeaderMenuItemsObj[menuItemKey]}
        </OrderDetailsInvoiceHeaderFilterItem>
      ))}
    </OrderDetailsInvoiceHeaderFilterWrapper>
  </OrderDetailsInvoiceHeaderWrapper>
);
OrderInvoiceHeader.propTypes = {
  stringsData: PropTypes.shape({
    orderDetailsContent: PropTypes.shape({
      orderInvoiceTitle: PropTypes.string,
      orderInvoiceFilterOriginal: PropTypes.string,
      orderInvoiceFilterDispensed: PropTypes.string,
      orderInvoiceFilterReturned: PropTypes.string,
      orderInvoiceFilterComparison: PropTypes.string,
    }),
  }).isRequired,
  selectedFilter: PropTypes.string.isRequired,
  invoiceHeaderMenuItemsObj: PropTypes.shape({}).isRequired,
  setSelectedFilter: PropTypes.func.isRequired,
};

const Invoice = ({
  orderInvoiceGridHeaderList,
  orderInvoiceGridHeaderKeys,
  servicesList,
  invoiceGridItemsToSizeMap,
  showInvoiceStockColumn,
  showInvoiceSubTotalColumn,
  showInvoiceQuantityColumn,
  showMinimalView,
  showFixedOrderSize,
  showChangeOrderSizeDropdown,
  showDeleteBtn,
  bigGridItemPadding,
  handleServiceItemDeleteBtnClick,
  handleChangeItemQuantity,
}) => {
  const invoiceGridSize = Object.keys(invoiceGridItemsToSizeMap)
    .map(itemSizeKey => invoiceGridItemsToSizeMap[itemSizeKey])
    .join(' ');

  return (
    <OrderDetailsInvoiceGridWrapper
      bigGridItemPadding={bigGridItemPadding}
      invoiceGridSize={invoiceGridSize}
    >
      {orderInvoiceGridHeaderKeys.map((orderInvoiceGridHeaderKey, index) => {
        const firstItem = index === 0;
        const lastItem = index === orderInvoiceGridHeaderKeys.length - 1;

        return (
          <OrderDetailsInvoiceGridHeader
            key={orderInvoiceGridHeaderKey}
            startPadding={firstItem}
            endPadding={lastItem}
            pushToEnd={orderInvoiceGridHeaderKey === 'invoiceTotalGridHeader'}
          >
            {orderInvoiceGridHeaderList[orderInvoiceGridHeaderKey]}
          </OrderDetailsInvoiceGridHeader>
        );
      })}

      {servicesList.map((item, index) => {
        const isEvenRecord = index % 2 === 0;
        const price = item.servicePrice;
        const newPrice = item.serviceNewPrice;
        return (
          <React.Fragment key={item.serviceKey}>
            <OrderDetailsInvoiceGridItem
              displayFlex
              startPadding
              endPadding
              coloredBackground={isEvenRecord}
            >
              {item.serviceName}
              {showDeleteBtn && (
                <OrderDetailsInvoiceGridItemIcon
                  onClick={() => handleServiceItemDeleteBtnClick(item.serviceKey)}
                >
                  X
                </OrderDetailsInvoiceGridItemIcon>
              )}
            </OrderDetailsInvoiceGridItem>
            {!showMinimalView && (
              <>
                {showInvoiceStockColumn && (
                  <OrderDetailsInvoiceGridItem coloredBackground={isEvenRecord}>
                    {item.productVezeetaStock?.toFixed(0)}
                  </OrderDetailsInvoiceGridItem>
                )}
                {showInvoiceStockColumn && (
                  <OrderDetailsInvoiceGridItem coloredBackground={isEvenRecord}>
                    {item.productBranchStock?.toFixed(0)}
                  </OrderDetailsInvoiceGridItem>
                )}
                <OrderDetailsInvoiceGridItem coloredBackground={isEvenRecord}>
                  {price.toFixed(2)}
                </OrderDetailsInvoiceGridItem>
              </>
            )}
            {showFixedOrderSize && showInvoiceQuantityColumn && (
              <OrderDetailsInvoiceGridItem coloredBackground={isEvenRecord}>
                {item.quantity} {item.productShapeName}
              </OrderDetailsInvoiceGridItem>
            )}
            {showChangeOrderSizeDropdown && showInvoiceQuantityColumn && !showFixedOrderSize && (
              <OrderDetailsInvoiceGridQuantityWrapper coloredBackground={isEvenRecord}>
                <OrderDetailsInvoiceGridChangeQuantityWrapper>
                  <OrderDetailsInvoiceGridChangeQuantityMinus
                    onClick={() => handleChangeItemQuantity(item.serviceKey, 'minus')}
                  />
                  <OrderDetailsInvoiceGridChangeQuantityValue>
                    {item.quantity} {item.productShapeName}
                  </OrderDetailsInvoiceGridChangeQuantityValue>
                  <OrderDetailsInvoiceGridChangeQuantityPlus
                    onClick={() => handleChangeItemQuantity(item.serviceKey, 'plus')}
                  />
                </OrderDetailsInvoiceGridChangeQuantityWrapper>
              </OrderDetailsInvoiceGridQuantityWrapper>
            )}
            {!showMinimalView && (
              <>
                {showInvoiceSubTotalColumn && (
                  <OrderDetailsInvoiceGridItem coloredBackground={isEvenRecord}>
                    {(price * (item.quantity || 1)).toFixed(2)}
                  </OrderDetailsInvoiceGridItem>
                )}
                <OrderDetailsInvoiceGridItem coloredBackground={isEvenRecord}>
                  {newPrice ? (price - newPrice).toFixed(2) : '-'}
                </OrderDetailsInvoiceGridItem>
                <OrderDetailsInvoiceGridItem endPadding pushToEnd coloredBackground={isEvenRecord}>
                  {((newPrice || price) * (item.quantity || 1)).toFixed(2)}
                </OrderDetailsInvoiceGridItem>
              </>
            )}
          </React.Fragment>
        );
      })}
    </OrderDetailsInvoiceGridWrapper>
  );
};
Invoice.propTypes = {
  orderInvoiceGridHeaderList: PropTypes.shape({}).isRequired,
  orderInvoiceGridHeaderKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  servicesList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  invoiceGridItemsToSizeMap: PropTypes.shape({}).isRequired,
  showInvoiceStockColumn: PropTypes.bool,
  showInvoiceSubTotalColumn: PropTypes.bool,
  showInvoiceQuantityColumn: PropTypes.bool,
  showMinimalView: PropTypes.bool,
  showFixedOrderSize: PropTypes.bool,
  showChangeOrderSizeDropdown: PropTypes.bool,
  showDeleteBtn: PropTypes.bool,
  bigGridItemPadding: PropTypes.bool,
  handleServiceItemDeleteBtnClick: PropTypes.func.isRequired,
  handleChangeItemQuantity: PropTypes.func.isRequired,
};
Invoice.defaultProps = {
  showInvoiceStockColumn: false,
  showInvoiceSubTotalColumn: false,
  showInvoiceQuantityColumn: false,
  showMinimalView: false,
  showFixedOrderSize: true,
  showChangeOrderSizeDropdown: false,
  showDeleteBtn: false,
  bigGridItemPadding: false,
};

const OrderInvoiceGrid = props => {
  const {
    selectedFilter,
    orderDetails,
    dispensedOrderItems,
    invoiceHeaderMenuItemsObj,
    orderStatusToEnableEditInvoice,
    permissionsCategories,
  } = props;

  const orderData = orderDetails?.details;
  const { orderStateTypeId: orderStatus, mappedServicesListSnapshot } = orderData;

  return (
    <>
      {selectedFilter === invoiceHeaderMenuItemsObj.original && (
        <Can
          permissionsCategory={permissionsCategories.order}
          perform="order:viewOrderItems"
          renderYes={() => (
            <Invoice {...props} servicesList={mappedServicesListSnapshot} showFixedOrderSize />
          )}
          renderNo={() => null}
        />
      )}
      {selectedFilter === invoiceHeaderMenuItemsObj.dispensed && (
        <Can
          permissionsCategory={permissionsCategories.order}
          perform="order:editOrderItems"
          renderYes={() => (
            <Invoice
              {...props}
              servicesList={dispensedOrderItems}
              showFixedOrderSize={!orderStatusToEnableEditInvoice.includes(orderStatus)}
              showChangeOrderSizeDropdown
              showDeleteBtn={orderStatusToEnableEditInvoice.includes(orderStatus)}
              bigGridItemPadding
            />
          )}
          renderNo={() => (
            <Invoice {...props} servicesList={mappedServicesListSnapshot} showFixedOrderSize />
          )}
        />
      )}
      {selectedFilter === invoiceHeaderMenuItemsObj.comparison && (
        <OrderDetailsInvoiceMultiGridWrapper>
          <Invoice
            {...props}
            servicesList={mappedServicesListSnapshot}
            showFixedOrderSize
            showMinimalView
          />
          <Invoice
            {...props}
            servicesList={dispensedOrderItems}
            showFixedOrderSize
            showMinimalView
          />
        </OrderDetailsInvoiceMultiGridWrapper>
      )}
    </>
  );
};
OrderInvoiceGrid.propTypes = {
  orderInvoiceGridHeaderList: PropTypes.shape({}).isRequired,
  orderInvoiceGridHeaderKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  orderDetails: PropTypes.shape({
    details: PropTypes.shape({
      mappedServicesListSnapshot: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }).isRequired,
  dispensedOrderItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedFilter: PropTypes.string.isRequired,
  invoiceHeaderMenuItemsObj: PropTypes.shape({
    original: PropTypes.string,
    dispensed: PropTypes.string,
    comparison: PropTypes.string,
  }).isRequired,
  orderStatusToEnableEditInvoice: PropTypes.arrayOf(PropTypes.number).isRequired,
  permissionsCategories: PropTypes.shape({
    order: PropTypes.string,
  }),
};

OrderInvoiceGrid.defaultProps = {
  permissionsCategories: {},
};

const SearchBarEnvelopContent = ({
  stringsData,
  orderServicesSuggestions,
  handleServicesSuggestionsItemClick,
  objectMapper,
}) => {
  const { status: servicesDataStatus } = orderServicesSuggestions;
  const servicesData = orderServicesSuggestions.details;

  if (servicesDataStatus === status.SHOULD_CALL_API || servicesDataStatus === status.FETCHING)
    return (
      <OrderDetailsInvoiceSearchBarLoaderWrapper>
        <Loader width={40} height={40} thickness={6} />
      </OrderDetailsInvoiceSearchBarLoaderWrapper>
    );

  if (
    servicesDataStatus === status.FAIL ||
    (servicesDataStatus === status.SUCCESS && servicesData?.length === 0)
  )
    return (
      <OrderDetailsInvoiceSearchBarLoaderWrapper>
        <OrderDetailsInvoiceSearchBarNoDataText>
          {stringsData.orderDetailsContent.orderInvoiceServicesNoResultText}
        </OrderDetailsInvoiceSearchBarNoDataText>
      </OrderDetailsInvoiceSearchBarLoaderWrapper>
    );

  return servicesData?.map(service => (
    <OrderDetailsInvoiceSearchBarItemWrapper
      key={objectMapper(service)?.key}
      onClick={() => handleServicesSuggestionsItemClick(service)}
      isSearchable={objectMapper(service)?.isSearchable}
    >
      <OrderDetailsInvoiceSearchBarItem>
        {objectMapper(service)?.name}
      </OrderDetailsInvoiceSearchBarItem>
      <OrderDetailsInvoiceSearchBarItemSubText>{`${objectMapper(service)?.price?.toFixed(2)} ${
        stringsData.orderDetailsContent.orderInvoiceServicesCurrency
      }`}</OrderDetailsInvoiceSearchBarItemSubText>
    </OrderDetailsInvoiceSearchBarItemWrapper>
  ));
};
SearchBarEnvelopContent.propTypes = {
  stringsData: PropTypes.shape({
    orderDetailsContent: PropTypes.shape({
      orderInvoiceServicesNoResultText: PropTypes.string,
      orderInvoiceServicesCurrency: PropTypes.string,
    }),
  }).isRequired,
  orderServicesSuggestions: PropTypes.shape({
    status: PropTypes.string,
    details: PropTypes.arrayOf(
      PropTypes.shape({
        Data: PropTypes.shape({
          Services: PropTypes.arrayOf(PropTypes.shape({})),
        }),
      }),
    ),
  }).isRequired,
  handleServicesSuggestionsItemClick: PropTypes.func.isRequired,
  objectMapper: PropTypes.func.isRequired,
};

const OrderInvoiceInfo = ({
  stringsData,
  themeContext,
  orderDetails,
  dispensedOrderItems,
  showInsuranceInvoiceText,
  showPromoCodeInvoiceText,
  showVezeetaPointsInvoiceText,
  showPromocodeInvoiceTextOperationSide,
  promoCodeReference,
  setPromoCode,
  selectedFilter,
  invoiceHeaderMenuItemsObj,
  orderStatus,
  orderStatusToEnableEditInvoice,
}) => {
  const orderData = orderDetails.details;

  const { orderDate, deliveryFee, promoCodeStatus, orderReceipt, hasPromoCodeAddedByPatient } =
    orderData;
  const orderLab = (orderData.selectedLabObj.key === '0' && '-') || orderData.selectedLabObj.name;
  const orderChemist =
    (orderData.selectedChemistObj.key === '0' && '-') || orderData.selectedChemistObj.name;

  const subTotal = dispensedOrderItems.reduce((prevValue, currentValue) => {
    let price = currentValue.servicePrice;
    if (currentValue?.quantity) price = currentValue.servicePrice * currentValue.quantity;
    return prevValue + price;
  }, 0);
  const discountAmount = orderReceipt?.discountAmount ?? 0;
  const vezeetaCashDiscountAmount = orderReceipt?.vezeetaCash ?? 0;
  const discountFee = discountAmount;
  const totalAmount = subTotal + deliveryFee - discountFee - vezeetaCashDiscountAmount;

  const handlePromoCode = () => {
    if (promoCodeStatusPending.includes(promoCodeStatus))
      return discountAmount ? `-${discountAmount.toFixed(2)} EGP` : '-';
    return '';
  };

  return (
    <OrderDetailsInvoiceInfoWrapper>
      <OrderDetailsInvoiceInfoFirstColumn>
        <OrderDetailsInvoiceInfoText>
          {`${stringsData.orderDetailsContent.orderInvoiceDetailsLabTitle}: ${orderLab}`}
        </OrderDetailsInvoiceInfoText>
        <SectionDivider gap={4} />
        <OrderDetailsInvoiceInfoText>
          {`${stringsData.orderDetailsContent.orderInvoiceDetailsChemistTitle}: ${orderChemist}`}
        </OrderDetailsInvoiceInfoText>
        <SectionDivider gap={4} />
        <OrderDetailsInvoiceInfoText>{orderDate.orderElapsedTimeStr}</OrderDetailsInvoiceInfoText>
      </OrderDetailsInvoiceInfoFirstColumn>
      <OrderDetailsInvoiceInfoSecondColumn>
        <OrderDetailsInvoiceInfoBlackText>
          {stringsData.orderDetailsContent.orderInvoiceDetailsSubtotalLabel(subTotal.toFixed(2))}
        </OrderDetailsInvoiceInfoBlackText>
        <SectionDivider gap={4} />
        <OrderDetailsInvoiceInfoBlackText>
          {stringsData.orderDetailsContent.orderInvoiceDetailsDeliveryFeeLabel(
            deliveryFee.toFixed(2),
          )}
        </OrderDetailsInvoiceInfoBlackText>
        <SectionDivider gap={4} />
        <OrderDetailsInvoiceInfoBlackText>
          {stringsData.orderDetailsContent.orderInvoiceDetailsDiscountLabel(discountFee.toFixed(2))}
        </OrderDetailsInvoiceInfoBlackText>
        <SectionDivider gap={4} />
        {showInsuranceInvoiceText && (
          <>
            <OrderDetailsInvoiceInfoIconText
              icon={"'\\e90d'"}
              textColor={themeContext.colors.greenMain}
              iconColor={themeContext.colors.greenMain}
            >
              {stringsData.orderDetailsContent.orderInvoiceDetailsInsuranceLabel}
            </OrderDetailsInvoiceInfoIconText>
            <SectionDivider gap={4} />
          </>
        )}
        {showPromoCodeInvoiceText && (
          <OrderInvoiceInfoPromoCode>
            {!hasPromoCodeAddedByPatient &&
              selectedFilter !== invoiceHeaderMenuItemsObj.original &&
              orderStatusToEnableEditInvoice.includes(orderStatus) && (
                <OrderInvoiceRemovePromoCodeItemIcon onClick={() => setPromoCode('')}>
                  X
                </OrderInvoiceRemovePromoCodeItemIcon>
              )}
            <OrderDetailsInvoiceInfoIconText
              icon={"'\\e911'"}
              iconColor={themeContext.colors.blueMain}
            >
              {stringsData.orderDetailsContent.orderInvoiceDetailsPromoCodeLabel(
                promoCodeReference,
                handlePromoCode(),
              )}
              {!promoCodeStatusPending.includes(promoCodeStatus) && (
                <InvalidPromoCodeText>
                  {stringsData.orderDetailsContent.orderPromoCodeIsInvalid}
                </InvalidPromoCodeText>
              )}
            </OrderDetailsInvoiceInfoIconText>
            <SectionDivider gap={4} />
          </OrderInvoiceInfoPromoCode>
        )}
        {showPromocodeInvoiceTextOperationSide && (
          <InputField
            value={promoCodeReference}
            type="text"
            placeholder={stringsData.orderDetailsContent.orderInvoiceDetailsPromocodePlaceholder}
            onChange={event => setPromoCode(event.target.value)}
          />
        )}
        {showVezeetaPointsInvoiceText && (
          <>
            <OrderDetailsInvoiceInfoIconText
              icon={"'\\e912'"}
              iconColor={themeContext.colors.orangeDark}
            >
              {stringsData.orderDetailsContent.orderInvoiceDetailsVezeetaPointsLabelWithoutPoints(
                orderReceipt?.vezeetaCash,
              )}
              {/* {stringsData.orderDetailsContent.orderInvoiceDetailsVezeetaPointsLabel(
                '50',
                '-15.00 EGP',
              )} */}
            </OrderDetailsInvoiceInfoIconText>
            <SectionDivider gap={4} />
          </>
        )}
        <OrderDetailsInvoiceInfoBigText>
          {stringsData.orderDetailsContent.orderInvoiceDetailsTotalLabel(totalAmount.toFixed(2))}
        </OrderDetailsInvoiceInfoBigText>
        <SectionDivider gap={4} />
        <OrderDetailsInvoiceInfoText>Payment by Cash on Delivery</OrderDetailsInvoiceInfoText>
      </OrderDetailsInvoiceInfoSecondColumn>
    </OrderDetailsInvoiceInfoWrapper>
  );
};
OrderInvoiceInfo.propTypes = {
  stringsData: PropTypes.shape({
    orderDetailsContent: PropTypes.shape({
      orderInvoiceDetailsLabTitle: PropTypes.string,
      orderInvoiceDetailsChemistTitle: PropTypes.string,
      orderInvoiceDetailsSubtotalLabel: PropTypes.func,
      orderInvoiceDetailsDeliveryFeeLabel: PropTypes.func,
      orderInvoiceDetailsDiscountLabel: PropTypes.func,
      orderInvoiceDetailsTotalLabel: PropTypes.func,
      orderInvoiceDetailsInsuranceLabel: PropTypes.string,
      orderInvoiceDetailsPromoCodeLabel: PropTypes.func,
      orderInvoiceDetailsVezeetaPointsLabel: PropTypes.func,
      orderInvoiceDetailsVezeetaPointsLabelWithoutPoints: PropTypes.func,
      orderPromoCodeIsInvalid: PropTypes.string,
      orderInvoiceDetailsPromocodePlaceholder: PropTypes.string,
    }),
  }).isRequired,
  themeContext: PropTypes.shape({
    colors: PropTypes.shape({
      greenMain: PropTypes.string,
      blueMain: PropTypes.string,
      orangeDark: PropTypes.string,
    }),
  }).isRequired,
  orderDetails: PropTypes.shape({
    details: PropTypes.shape({
      deliveryFee: PropTypes.number,
      orderDate: PropTypes.shape({
        orderElapsedTimeStr: PropTypes.string,
      }),
      selectedLabObj: PropTypes.shape({
        key: PropTypes.string,
        name: PropTypes.string,
      }),
      selectedChemistObj: PropTypes.shape({
        key: PropTypes.string,
        name: PropTypes.string,
      }),
      promoCode: PropTypes.string,
      orderReceipt: PropTypes.shape({
        discountAmount: PropTypes.number,
        vezeetaCash: PropTypes.number,
      }),
      promoCodeStatus: PropTypes.number,
      hasPromoCodeAddedByPatient: PropTypes.bool,
    }),
  }).isRequired,
  promoCodeReference: PropTypes.string.isRequired,
  setPromoCode: PropTypes.func.isRequired,
  dispensedOrderItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  showInsuranceInvoiceText: PropTypes.bool,
  showPromoCodeInvoiceText: PropTypes.bool,
  showVezeetaPointsInvoiceText: PropTypes.bool,
  showPromocodeInvoiceTextOperationSide: PropTypes.bool,
  selectedFilter: PropTypes.string.isRequired,
  invoiceHeaderMenuItemsObj: PropTypes.shape({
    original: PropTypes.string,
  }).isRequired,
  orderStatus: PropTypes.number.isRequired,
  orderStatusToEnableEditInvoice: PropTypes.arrayOf(PropTypes.number).isRequired,
};
OrderInvoiceInfo.defaultProps = {
  showInsuranceInvoiceText: false,
  showPromoCodeInvoiceText: false,
  showVezeetaPointsInvoiceText: false,
  showPromocodeInvoiceTextOperationSide: false,
};
// ###############################################################
// <-------------------- End Sub Sections ----------------------->
// ###############################################################
// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const OrderInvoice = ({
  stringsData,
  orderDetails,
  orderServicesSuggestions,
  orderServicesSubmission,
  showInvoiceStockColumn,
  showInvoiceSubTotalColumn,
  showInvoiceQuantityColumn,
  getServicesSuggestionsData,
  resetServicesSuggestionsData,
  submitOrderInvoiceDispensedItems,
  orderStatusToEnableEditInvoice,
  objectMapper,
  permissionsCategories,
}) => {
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  const dispatch = useDispatch();

  const themeContext = useContext(ThemeContext);

  const orderData = orderDetails.details;
  const { orderStateTypeId: orderStatus } = orderData;
  const { mappedServicesList, mappedServicesListSnapshot, orderReceipt } = orderData;

  const invoiceHeaderMenuItemsObj = {
    original: stringsData.orderDetailsContent.orderInvoiceFilterOriginal,
    dispensed: stringsData.orderDetailsContent.orderInvoiceFilterDispensed,
    comparison: stringsData.orderDetailsContent.orderInvoiceFilterComparison,
  };
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  const [selectedFilter, setSelectedFilter] = useState(invoiceHeaderMenuItemsObj.original);
  const [dispensedOrderItems, setDispensedOrderItems] = useState(mappedServicesList);
  const [searchInputQuery, setSearchInputQuery] = useState('');
  const [isEnvelopOpen, setIsEnvelopOpen] = useState(false);
  const [promoCodeReference, setPromoCode] = useState('');
  const [isPromoCodeInputAvailable, setIsPromoCodeInputAvailable] = useState(false);
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  useEffect(() => {
    const mappedServicesListUpdated = orderDetails.details.mappedServicesList;
    setDispensedOrderItems(mappedServicesListUpdated);
    setPromoCode(orderDetails.details.promoCode);
    if (orderDetails?.details?.promoCode) setIsPromoCodeInputAvailable(false);
  }, [orderDetails]);

  useEffect(() => {
    dispatch(setOrderDispensedItemsDetails(dispensedOrderItems));
  }, [dispatch, dispensedOrderItems]);

  useEffect(() => {
    if (promoCodeReference === '') setIsPromoCodeInputAvailable(true);
  }, [promoCodeReference]);
  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  const servicesSearchBarTimeout = useRef(null);
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  const getServicesSuggestions = query => {
    setSearchInputQuery(query);

    if (servicesSearchBarTimeout.current) {
      clearTimeout(servicesSearchBarTimeout.current);
    }

    if (query && query.length >= 2) {
      servicesSearchBarTimeout.current = setTimeout(() => {
        clearTimeout(servicesSearchBarTimeout.current);

        getServicesSuggestionsData(query);
      }, 300);
    }
  };
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  const handleServicesSuggestionsInputChange = event => {
    const { value } = event.target;

    if (value.length > 30) return;
    setSearchInputQuery(value);

    if (value && value.length >= 2) {
      setIsEnvelopOpen(true);
      getServicesSuggestions(value);
    } else {
      setIsEnvelopOpen(false);
      resetServicesSuggestionsData();
    }
  };

  const handleServicesSuggestionsItemClick = item => {
    if (dispensedOrderItems.some(service => service.serviceKey === objectMapper(item).key)) return;

    setIsEnvelopOpen(false);
    setSearchInputQuery('');
    resetServicesSuggestionsData();
    setDispensedOrderItems(prev => [
      ...prev,
      {
        serviceKey: objectMapper(item).key,
        serviceName: objectMapper(item).name,
        servicePrice: objectMapper(item).price,
        ...(showInvoiceQuantityColumn && {
          quantity: 1,
          productShapeId: objectMapper(item).productShapeId,
          productShapeName: objectMapper(item).productShapeName,
        }),
      },
    ]);
  };

  const handleServiceItemDeleteBtnClick = itemKey => {
    setDispensedOrderItems(prev => prev.filter(service => service.serviceKey !== itemKey));
  };

  const handleChangeItemQuantity = (itemKey, operationType) => {
    const tempDispensedOrderItems = [...dispensedOrderItems];
    const tempItemIndex = tempDispensedOrderItems.findIndex(
      service => service.serviceKey === itemKey,
    );
    const tempItem = tempDispensedOrderItems.find(service => service.serviceKey === itemKey);
    if (operationType === 'plus') tempItem.quantity += 1;
    else if (operationType === 'minus' && tempItem.quantity > 1) tempItem.quantity -= 1;
    tempDispensedOrderItems.splice(tempItemIndex, 1, tempItem);
    setDispensedOrderItems([...tempDispensedOrderItems]);
  };

  const handleInvoiceSubmitBtnClick = () => {
    const dispensedOrderItemsArr = dispensedOrderItems.map(item => ({
      productKey: item.serviceKey,
      note: '',
      ...(item?.quantity && {
        quantity: item?.quantity,
      }),
      ...(item?.productShapeId && {
        productShapeId: item?.productShapeId,
      }),
    }));
    const invoicePayload = {
      orderItems: dispensedOrderItemsArr,
      promoCode: promoCodeReference,
    };
    submitOrderInvoiceDispensedItems(invoicePayload);
  };
  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################
  if (
    (typeof mappedServicesList === 'string' && mappedServicesList === status.FETCHING) ||
    (typeof dispensedOrderItems === 'string' && dispensedOrderItems === status.FETCHING) ||
    (typeof orderDetails.details.selectedLabObj === 'string' &&
      orderDetails.details.selectedLabObj === status.FETCHING)
  )
    return (
      <OrderDetailsInvoiceSection padding>
        <Loader
          width={50}
          height={50}
          thickness={6}
          extendStyle={extendOrderDetailsInvoiceLoaderStyle}
        />
      </OrderDetailsInvoiceSection>
    );

  if (
    (typeof mappedServicesList === 'string' && mappedServicesList === status.FAIL) ||
    (typeof dispensedOrderItems === 'string' && dispensedOrderItems === status.FAIL)
  )
    return (
      <OrderDetailsInvoiceSection padding>
        <OrderDetailsInvoiceTitle>
          {stringsData.orderDetailsContent.noDataPlaceholder}
        </OrderDetailsInvoiceTitle>
      </OrderDetailsInvoiceSection>
    );

  const orderInvoiceGridHeaderList = stringsData.orderDetailsContent.orderInvoiceGridHeader;
  let orderInvoiceGridHeaderKeys = Object.keys(orderInvoiceGridHeaderList);

  // any column we need to hide from the grid we need to do 3 steps:
  // 1. remove the column header from header list
  // 2. hide the column items in 'Invoice' component
  // 3. remove the column from 'invoiceGridItemsToSizeMap' to render the correct grid size
  const invoiceGridItemsToSizeMap = {
    invoiceItemGridHeader: '35fr',
    invoiceVStockGridHeader: '7fr',
    invoiceBStockGridHeader: '7fr',
    invoicePriceGridHeader: '7fr',
    invoiceQuantityGridHeader: '25fr',
    invoiceSubTotalGridHeader: '7fr',
    invoiceDiscountGridHeader: '7fr',
    invoiceTotalGridHeader: '12fr',
  };
  if (!showInvoiceStockColumn) {
    orderInvoiceGridHeaderKeys = orderInvoiceGridHeaderKeys.filter(
      item => item !== 'invoiceVStockGridHeader' && item !== 'invoiceBStockGridHeader',
    );
    delete invoiceGridItemsToSizeMap.invoiceVStockGridHeader;
    delete invoiceGridItemsToSizeMap.invoiceBStockGridHeader;
  }
  if (!showInvoiceSubTotalColumn) {
    orderInvoiceGridHeaderKeys = orderInvoiceGridHeaderKeys.filter(
      item => item !== 'invoiceSubTotalGridHeader',
    );
    delete invoiceGridItemsToSizeMap.invoiceSubTotalGridHeader;
  }
  if (!showInvoiceQuantityColumn) {
    orderInvoiceGridHeaderKeys = orderInvoiceGridHeaderKeys.filter(
      item => item !== 'invoiceQuantityGridHeader',
    );
    delete invoiceGridItemsToSizeMap.invoiceQuantityGridHeader;
  }

  if (selectedFilter === invoiceHeaderMenuItemsObj.comparison) {
    orderInvoiceGridHeaderKeys = orderInvoiceGridHeaderKeys.filter(
      item =>
        ['invoiceItemGridHeader'].includes(item) || ['invoiceQuantityGridHeader'].includes(item),
    );
    delete invoiceGridItemsToSizeMap.invoiceVStockGridHeader;
    delete invoiceGridItemsToSizeMap.invoiceBStockGridHeader;
    delete invoiceGridItemsToSizeMap.invoicePriceGridHeader;
    delete invoiceGridItemsToSizeMap.invoiceSubTotalGridHeader;
    delete invoiceGridItemsToSizeMap.invoiceDiscountGridHeader;
    delete invoiceGridItemsToSizeMap.invoiceTotalGridHeader;
  }

  return (
    <OrderDetailsInvoiceSection>
      <OrderInvoiceHeader
        stringsData={stringsData}
        selectedFilter={selectedFilter}
        invoiceHeaderMenuItemsObj={invoiceHeaderMenuItemsObj}
        setSelectedFilter={setSelectedFilter}
      />
      {((mappedServicesList.length > 0 ||
        mappedServicesListSnapshot.length > 0 ||
        dispensedOrderItems.length > 0) && (
        <OrderInvoiceGrid
          orderInvoiceGridHeaderList={orderInvoiceGridHeaderList}
          orderInvoiceGridHeaderKeys={orderInvoiceGridHeaderKeys}
          orderDetails={orderDetails}
          dispensedOrderItems={dispensedOrderItems}
          selectedFilter={selectedFilter}
          invoiceHeaderMenuItemsObj={invoiceHeaderMenuItemsObj}
          invoiceGridItemsToSizeMap={invoiceGridItemsToSizeMap}
          handleServiceItemDeleteBtnClick={handleServiceItemDeleteBtnClick}
          handleChangeItemQuantity={handleChangeItemQuantity}
          showInvoiceStockColumn={showInvoiceStockColumn}
          showInvoiceSubTotalColumn={showInvoiceSubTotalColumn}
          showInvoiceQuantityColumn={showInvoiceQuantityColumn}
          orderStatusToEnableEditInvoice={orderStatusToEnableEditInvoice}
          objectMapper={objectMapper}
        />
      )) ||
        ''}
      {selectedFilter === invoiceHeaderMenuItemsObj.dispensed &&
        orderStatusToEnableEditInvoice.includes(orderStatus) && (
          <Can
            permissionsCategory={permissionsCategories?.order}
            perform="order:editOrderItems"
            renderYes={() => (
              <OrderDetailsInvoiceSearchBarWrapper>
                <OrderDetailsInvoiceSearchBarInputWrapper>
                  <OrderDetailsInvoiceSearchBarIcon />
                  <OrderDetailsInvoiceSearchBar
                    placeholder={stringsData.orderDetailsContent.orderInvoiceSearchBarPlaceholder}
                    value={searchInputQuery}
                    onChange={event => handleServicesSuggestionsInputChange(event)}
                  />
                </OrderDetailsInvoiceSearchBarInputWrapper>
                <OrderDetailsInvoiceSearchBarEnvelop open={isEnvelopOpen}>
                  <SearchBarEnvelopContent
                    stringsData={stringsData}
                    orderServicesSuggestions={orderServicesSuggestions}
                    handleServicesSuggestionsItemClick={handleServicesSuggestionsItemClick}
                    objectMapper={objectMapper}
                  />
                </OrderDetailsInvoiceSearchBarEnvelop>
              </OrderDetailsInvoiceSearchBarWrapper>
            )}
            no={() => null}
          />
        )}
      <OrderInvoiceInfo
        stringsData={stringsData}
        themeContext={themeContext}
        orderDetails={orderDetails}
        dispensedOrderItems={dispensedOrderItems}
        showPromoCodeInvoiceText={
          orderDetails.details.promoCode === promoCodeReference && !!orderDetails.details.promoCode
        }
        showPromocodeInvoiceTextOperationSide={
          isPromoCodeInputAvailable &&
          orderStatusToEnableAddingPromoCode.includes(orderStatus) &&
          (selectedFilter === invoiceHeaderMenuItemsObj.dispensed ||
            selectedFilter === invoiceHeaderMenuItemsObj.comparison)
        }
        promoCodeReference={promoCodeReference}
        setPromoCode={setPromoCode}
        selectedFilter={selectedFilter}
        invoiceHeaderMenuItemsObj={invoiceHeaderMenuItemsObj}
        orderStatus={orderStatus}
        orderStatusToEnableEditInvoice={orderStatusToEnableEditInvoice}
        showVezeetaPointsInvoiceText={!!orderReceipt?.vezeetaCash}
      />
      {(selectedFilter === invoiceHeaderMenuItemsObj.dispensed ||
        selectedFilter === invoiceHeaderMenuItemsObj.comparison) &&
        orderStatusToEnableEditInvoice.includes(orderStatus) && (
          <Can
            permissionsCategory={permissionsCategories?.order}
            perform="order:editOrderItems"
            renderYes={() => (
              <OrderDetailsInvoiceBtnWrapper>
                <OrderDetailsInvoiceBtn as="button" onClick={() => handleInvoiceSubmitBtnClick()}>
                  {(orderServicesSubmission.status === status.FETCHING && (
                    <Loader width={14} height={14} thickness={4} />
                  )) ||
                    stringsData.orderDetailsContent.orderInvoiceBtn}
                </OrderDetailsInvoiceBtn>
              </OrderDetailsInvoiceBtnWrapper>
            )}
            no={() => null}
          />
        )}
    </OrderDetailsInvoiceSection>
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

OrderInvoice.propTypes = {
  stringsData: PropTypes.shape({
    orderDetailsContent: PropTypes.shape({
      noDataPlaceholder: PropTypes.string,
      orderInvoiceFilterOriginal: PropTypes.string,
      orderInvoiceFilterDispensed: PropTypes.string,
      orderInvoiceFilterComparison: PropTypes.string,
      orderInvoiceGridHeader: PropTypes.shape({}),
      orderInvoiceSearchBarPlaceholder: PropTypes.string,
      orderInvoiceBtn: PropTypes.string,
    }),
  }).isRequired,
  orderDetails: PropTypes.shape({
    details: PropTypes.shape({
      orderStateTypeId: PropTypes.number,
      selectedLabObj: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
      mappedServicesList: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.shape({})),
      ]),
      mappedServicesListSnapshot: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.shape({})),
      ]),
      promoCode: PropTypes.string,
      hasPromoCodeAddedByPatient: PropTypes.bool.isRequired,
      orderReceipt: PropTypes.shape({
        vezeetaCash: PropTypes.number,
      }),
    }),
  }).isRequired,
  showInvoiceStockColumn: PropTypes.bool,
  showInvoiceSubTotalColumn: PropTypes.bool,
  showInvoiceQuantityColumn: PropTypes.bool,
  orderServicesSuggestions: PropTypes.shape({}).isRequired,
  orderServicesSubmission: PropTypes.shape({
    status: PropTypes.string,
  }).isRequired,
  getServicesSuggestionsData: PropTypes.func.isRequired,
  resetServicesSuggestionsData: PropTypes.func.isRequired,
  submitOrderInvoiceDispensedItems: PropTypes.func.isRequired,
  orderStatusToEnableEditInvoice: PropTypes.arrayOf(PropTypes.number).isRequired,
  objectMapper: PropTypes.func.isRequired,
  permissionsCategories: PropTypes.shape({
    order: PropTypes.string,
  }),
};

OrderInvoice.defaultProps = {
  showInvoiceStockColumn: false,
  showInvoiceSubTotalColumn: false,
  showInvoiceQuantityColumn: false,
  permissionsCategories: {},
};

export default OrderInvoice;
